import { useState, useCallback } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { mdiDragVertical, mdiPencil, mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { OperationEditDialog, type NormalizationOp } from './OperationEditDialog'
import { useCan } from '../../contexts/PermissionsContext'
import { EmptyState } from '../ui/empty-state'

const OP_LABELS: Record<string, string> = {
  trim: 'Trim whitespace',
  lowercase: 'Convert to lowercase',
  uppercase: 'Convert to uppercase',
  regex_replace: 'Regex replace',
  map: 'Dictionary lookup',
  number_parse: 'Parse number',
  boolean_parse: 'Parse boolean',
  script: 'JavaScript transform',
}

const ALL_OPS = ['trim', 'lowercase', 'uppercase', 'regex_replace', 'map', 'number_parse', 'boolean_parse', 'script']

function getOpSummary(op: NormalizationOp): string {
  switch (op.op) {
    case 'trim': return 'Remove leading/trailing whitespace'
    case 'lowercase': return 'Convert to lowercase'
    case 'uppercase': return 'Convert to uppercase'
    case 'regex_replace': return `/${op.pattern}/ → "${op.replacement || ''}"`
    case 'map': {
      const count = Object.keys(op.values || {}).length
      return `Dictionary lookup (${count} ${count === 1 ? 'entry' : 'entries'})${op.strict ? ' [strict]' : ''}`
    }
    case 'number_parse': {
      const parts = []
      if (op.integer) parts.push('integer')
      if (op.decimal_places !== undefined) parts.push(`${op.decimal_places} decimals`)
      return parts.length ? `Parse number (${parts.join(', ')})` : 'Parse number'
    }
    case 'boolean_parse': return 'Parse boolean (true/false, yes/no, 1/0)'
    case 'script': {
      const lines = (op.script || '').split('\n').length
      return `Custom JavaScript (${lines} ${lines === 1 ? 'line' : 'lines'})`
    }
    default: return op.op
  }
}

interface SortableCardProps {
  id: string
  index: number
  op: NormalizationOp
  canWrite: boolean
  onEdit: () => void
  onDelete: () => void
}

function SortableCard({ id, index, op, canWrite, onEdit, onDelete }: SortableCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-card rounded-lg border border-border p-4"
    >
      <div className="flex items-start gap-3">
        {canWrite && (
          <button
            className="mt-1 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
            {...attributes}
            {...listeners}
            data-testid={`pipeline-drag-${index}`}
          >
            <AppIcon path={mdiDragVertical} size="sm" tone="muted" />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="secondary" className="font-mono text-xs">
              {index + 1}. {op.op}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{getOpSummary(op)}</p>

          {/* Show script preview */}
          {op.op === 'script' && op.script && (
            <pre className="mt-2 text-xs font-mono bg-muted p-2 rounded max-h-24 overflow-y-auto whitespace-pre-wrap">
              {op.script}
            </pre>
          )}

          {/* Show map table if map op with entries */}
          {op.op === 'map' && op.values && Object.keys(op.values).length > 0 && (
            <div className="mt-2 max-h-40 overflow-y-auto rounded border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs py-1 px-2">Key</TableHead>
                    <TableHead className="text-xs py-1 px-2">Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(op.values).slice(0, 5).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell className="py-1 px-2 font-mono text-xs">{key}</TableCell>
                      <TableCell className="py-1 px-2 font-mono text-xs">{value}</TableCell>
                    </TableRow>
                  ))}
                  {Object.keys(op.values).length > 5 && (
                    <TableRow>
                      <TableCell colSpan={2} className="py-1 px-2 text-xs text-muted-foreground text-center">
                        ... and {Object.keys(op.values).length - 5} more
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
        {canWrite && (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onEdit}
              data-testid={`pipeline-edit-${index}`}
            >
              <AppIcon path={mdiPencil} size="xs" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onDelete}
              data-testid={`pipeline-delete-${index}`}
            >
              <AppIcon path={mdiTrashCanOutline} size="xs" className="text-destructive" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

interface PipelineEditorProps {
  pipeline: NormalizationOp[]
  onPipelineChange: (pipeline: NormalizationOp[]) => void
  isPending?: boolean
}

export function PipelineEditor({ pipeline, onPipelineChange, isPending }: PipelineEditorProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null)
  const [addOpType, setAddOpType] = useState<string>('')
  // The pipeline is part of the normalization definition: reordering, editing,
  // adding and deleting operations all need `configuration.write`. The
  // operation cards themselves stay visible (spec 2026-09-04-02, "Gating").
  const canWriteConfiguration = useCan('configuration.write')

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const itemIds = pipeline.map((_, i) => `op-${i}`)

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = itemIds.indexOf(active.id as string)
      const newIndex = itemIds.indexOf(over.id as string)
      const newPipeline = [...pipeline]
      const [moved] = newPipeline.splice(oldIndex, 1)
      newPipeline.splice(newIndex, 0, moved)
      onPipelineChange(newPipeline)
    }
  }, [pipeline, onPipelineChange, itemIds])

  const handleEdit = (index: number) => setEditingIndex(index)

  const handleEditSave = useCallback((op: NormalizationOp) => {
    if (editingIndex === null) return
    const newPipeline = [...pipeline]
    newPipeline[editingIndex] = op
    onPipelineChange(newPipeline)
    setEditingIndex(null)
  }, [editingIndex, pipeline, onPipelineChange])

  const handleDelete = useCallback(() => {
    if (deleteIndex === null) return
    const newPipeline = pipeline.filter((_, i) => i !== deleteIndex)
    onPipelineChange(newPipeline)
    setDeleteIndex(null)
  }, [deleteIndex, pipeline, onPipelineChange])

  const handleAdd = useCallback(() => {
    if (!addOpType) return
    const newOp: NormalizationOp = { op: addOpType }
    if (addOpType === 'map') newOp.values = {}
    if (addOpType === 'regex_replace') {
      newOp.pattern = ''
      newOp.replacement = ''
    }
    onPipelineChange([...pipeline, newOp])
    setAddOpType('')
  }, [addOpType, pipeline, onPipelineChange])

  return (
    <div className="space-y-4">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {pipeline.map((op, index) => (
              <SortableCard
                key={`op-${index}`}
                id={`op-${index}`}
                index={index}
                op={op}
                canWrite={canWriteConfiguration}
                onEdit={() => handleEdit(index)}
                onDelete={() => setDeleteIndex(index)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {pipeline.length === 0 && (
        <EmptyState title="No operations in the pipeline. Add one below." />
      )}

      {/* Add Operation */}
      {canWriteConfiguration && (
        <div className="flex gap-2">
          <Select value={addOpType} onValueChange={setAddOpType}>
            <SelectTrigger className="w-[250px]" data-testid="pipeline-add-op-select">
              <SelectValue placeholder="Select operation type..." />
            </SelectTrigger>
            <SelectContent>
              {ALL_OPS.map(op => (
                <SelectItem key={op} value={op}>
                  {OP_LABELS[op] || op}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleAdd}
            disabled={!addOpType || isPending}
            data-testid="pipeline-add-op-btn"
          >
            <AppIcon path={mdiPlus} size="sm" className="mr-1" />
            Add
          </Button>
        </div>
      )}

      {/* Edit Dialog */}
      <OperationEditDialog
        open={editingIndex !== null}
        onOpenChange={(open) => { if (!open) setEditingIndex(null) }}
        operation={editingIndex !== null ? pipeline[editingIndex] : null}
        onSave={handleEditSave}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteIndex !== null} onOpenChange={(open) => { if (!open) setDeleteIndex(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Operation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this "{deleteIndex !== null ? pipeline[deleteIndex]?.op : ''}" operation from the pipeline?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-op">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-op"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
